/**
 * OptimalRoiCard — answers "what ROI/target should I realistically expect?"
 * Realized winner-R percentiles + payoff ratio + break-even win-rate (now),
 * and an MFE-based optimal-target sweep that lights up as trades accumulate.
 */
import React from 'react';
import { Box, Card, CardContent, CardHeader, Typography, Tooltip, Chip } from '@mui/material';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip as RTooltip, ReferenceLine, CartesianGrid } from 'recharts';
import { useAppTheme } from '../../hooks/useAppTheme';
import { OptimalRoi } from '../../services/analytics.service';

const Stat: React.FC<{ label: string; value: string; tip?: string; color?: string }> = ({ label, value, tip, color }) => (
  <Tooltip arrow title={tip || ''}>
    <Box sx={{ minWidth: 92 }}>
      <Typography sx={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary' }}>{label}</Typography>
      <Typography sx={{ fontSize: '1.0rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: color || 'text.primary' }}>{value}</Typography>
    </Box>
  </Tooltip>
);

const OptimalRoiCard: React.FC<{ roi: OptimalRoi }> = ({ roi }) => {
  const { C } = useAppTheme();
  const pf = roi.payoffRatio;
  const hasMfe = roi.captureEfficiencyPct != null && roi.targetSweep.length > 0;

  return (
    <Card sx={{ borderRadius: 1.5, height: '100%' }}>
      <CardHeader
        title={<Typography fontWeight={700}>Optimal ROI &amp; exit efficiency</Typography>}
        subheader={<Typography variant="caption" color="text.secondary">What a realistic winner looks like, and the target that maximises edge</Typography>}
        sx={{ pb: 0.5 }}
      />
      <CardContent>
        <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', mb: 2 }}>
          <Stat label="Median win" value={`+${roi.winRPercentiles.p50.toFixed(2)}R`} color={C.bull} tip="Half your winners beat this R-multiple" />
          <Stat label="Top quartile" value={`+${roi.winRPercentiles.p75.toFixed(2)}R`} color={C.bull} tip="75th percentile winner" />
          <Stat label="Avg win / loss" value={`+${roi.avgWinR.toFixed(2)} / ${roi.avgLossR.toFixed(2)}R`} tip="Average winning vs losing R-multiple" />
          <Stat label="Payoff" value={Number.isFinite(pf) ? `${pf.toFixed(2)}×` : '∞'} tip="Average ₹win ÷ average ₹loss" />
          <Stat label="Break-even WR" value={`${roi.breakevenWinRate.toFixed(0)}%`} tip="Win-rate needed just to break even at this payoff. Beat it and you profit." />
          {roi.leftOnTableR != null && (
            <Stat label="Left on table" value={`${roi.leftOnTableR >= 0 ? '+' : ''}${roi.leftOnTableR.toFixed(2)}R`} color={C.warn}
              tip="Average R you gave back after the peak (avg MFE − avg realized). Big = you're exiting winners too early or too loose." />
          )}
          {roi.avgHeatR != null && (
            <Stat label="Avg heat (MAE)" value={`${roi.avgHeatR.toFixed(2)}R`} color={C.bear}
              tip="Typical adverse excursion the trade endured before its outcome — how much heat you took. Accrues on new trades." />
          )}
        </Box>

        {hasMfe ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Chip size="small" label={`Capture ${roi.captureEfficiencyPct}%`}
                sx={{ fontWeight: 700, bgcolor: (roi.captureEfficiencyPct! >= 60 ? C.bullMuted : C.warnMuted), color: roi.captureEfficiencyPct! >= 60 ? C.bull : C.warn }} />
              {roi.suggestedTargetR != null && (
                <Chip size="small" label={`Suggested target ≈ ${roi.suggestedTargetR}R`} sx={{ fontWeight: 700, bgcolor: C.accentMuted, color: C.accent }} />
              )}
              <Typography variant="caption" color="text.secondary">expectancy vs fixed target (from MFE)</Typography>
            </Box>
            <Box sx={{ height: 150 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={roi.targetSweep}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.chartGrid} />
                  <XAxis dataKey="r" stroke={C.dim} fontSize={10} tickFormatter={(v) => `${v}R`} />
                  <YAxis stroke={C.dim} fontSize={10} tickFormatter={(v) => `${v}R`} />
                  <RTooltip contentStyle={{ background: C.chartTooltipBg, border: `1px solid ${C.chartTooltipBorder}` }}
                    formatter={(v: any) => [`${Number(v).toFixed(2)}R`, 'Expectancy']} labelFormatter={(l) => `Target ${l}R`} />
                  <ReferenceLine y={0} stroke={C.dim} />
                  {roi.suggestedTargetR != null && <ReferenceLine x={roi.suggestedTargetR} stroke={C.accent} strokeDasharray="4 2" />}
                  <Area type="monotone" dataKey="expectancy" stroke={C.chartLine1} fill={C.chartLine1} fillOpacity={0.22} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </>
        ) : (
          <Box sx={{ p: 1.5, borderRadius: 1.5, border: `1px dashed ${C.neutralBorder}`, bgcolor: C.neutralMuted }}>
            <Typography variant="body2" sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
              <b>Exit-efficiency & optimal-target</b> learn from the favourable excursion (MFE) of each trade.
              Coverage so far: <b>{roi.mfeCoveragePct}%</b> of trades. This sharpens automatically as new
              auto-trades accumulate MFE data.
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default OptimalRoiCard;
